import React from 'react';
import { AlertCircle, Clock, Calendar, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const AlertasVencimento = ({ resumo, tipo }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  if (!resumo) return null;

  const dados = resumo[tipo];
  const totais = resumo.totais[tipo];

  const temAlertas = dados.vencidos.length > 0 || dados.hoje.length > 0 || dados.proximos_7_dias.length > 0;

  if (!temAlertas) {
    return (
      <Card className="border-2 border-green-300 bg-green-50">
        <CardContent className="py-4">
          <p className="text-center text-green-700 font-semibold flex items-center justify-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            Nenhum alerta de vencimento! Tudo em dia 🎉
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 flex items-center">
        <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
        Alertas de Vencimento
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Vencidos */}
        <Card className={`border-2 ${dados.vencidos.length > 0 ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-red-800 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              Vencidos ({dados.vencidos.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-900 mb-3">
              {formatCurrency(totais.vencidos)}
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {dados.vencidos.slice(0, 5).map((item, idx) => (
                <div key={idx} className="text-xs bg-white p-2 rounded border border-red-200">
                  <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                  <div className="flex justify-between mt-1">
                    <span className="text-red-700 font-bold">{formatCurrency(item.valor)}</span>
                    <span className="text-red-600">Há {Math.abs(item.dias)} dias</span>
                  </div>
                </div>
              ))}
              {dados.vencidos.length === 0 && (
                <p className="text-sm text-slate-500 italic text-center py-2">Nenhuma conta vencida</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Vence Hoje */}
        <Card className={`border-2 ${dados.hoje.length > 0 ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-slate-50'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-orange-800 flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Vence Hoje ({dados.hoje.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-900 mb-3">
              {formatCurrency(totais.hoje)}
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {dados.hoje.slice(0, 5).map((item, idx) => (
                <div key={idx} className="text-xs bg-white p-2 rounded border border-orange-200">
                  <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                  <div className="flex justify-between mt-1">
                    <span className="text-orange-700 font-bold">{formatCurrency(item.valor)}</span>
                    <span className="text-orange-600">Hoje</span>
                  </div>
                </div>
              ))}
              {dados.hoje.length === 0 && (
                <p className="text-sm text-slate-500 italic text-center py-2">Nenhuma conta vence hoje</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Próximos 7 Dias */}
        <Card className={`border-2 ${dados.proximos_7_dias.length > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-slate-200 bg-slate-50'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-yellow-800 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Próximos 7 Dias ({dados.proximos_7_dias.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-900 mb-3">
              {formatCurrency(totais.proximos_7_dias)}
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {dados.proximos_7_dias.slice(0, 5).map((item, idx) => (
                <div key={idx} className="text-xs bg-white p-2 rounded border border-yellow-200">
                  <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                  <div className="flex justify-between mt-1">
                    <span className="text-yellow-700 font-bold">{formatCurrency(item.valor)}</span>
                    <span className="text-yellow-600">Em {item.dias} dias</span>
                  </div>
                </div>
              ))}
              {dados.proximos_7_dias.length === 0 && (
                <p className="text-sm text-slate-500 italic text-center py-2">Nenhuma conta nos próximos 7 dias</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AlertasVencimento;
